import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { EarlyAccess } from "@/components/EarlyAccess";
import { useLaunchStatus } from "@/hooks/useLaunchStatus";
import api, { formatApiError } from "@/lib/api";

const features = [
  { heading: "AI support chatbots", body: "Resolve customer questions instantly with AI assistants trained on your business." },
  { heading: "Security operations monitoring", body: "Continuous security operations monitoring across your logs, events, and alerts." },
  { heading: "Real-time threat alerts", body: "Detect and surface suspicious activity the moment it happens." },
  { heading: "Launch in minutes", body: "Stand up a branded support and monitoring desk without the overhead." },
];

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/securedesk/waitlist", form);
      toast.success(data.message || "You're on the list.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p className="mt-8 text-sm text-green-400" data-testid="securedesk-waitlist-done">
        You're registered for SecureDesk early access. We'll email you at launch.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 max-w-md space-y-3" data-testid="securedesk-waitlist-form">
      <input
        required
        placeholder="Full name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        data-testid="securedesk-waitlist-name"
        className="w-full rounded-xl bg-surface border border-white/10 px-4 py-3 text-sm text-paper outline-none focus:border-white/30"
      />
      <input
        required
        type="email"
        placeholder="Work email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        data-testid="securedesk-waitlist-email"
        className="w-full rounded-xl bg-surface border border-white/10 px-4 py-3 text-sm text-paper outline-none focus:border-white/30"
      />
      <input
        placeholder="Company (optional)"
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
        data-testid="securedesk-waitlist-company"
        className="w-full rounded-xl bg-surface border border-white/10 px-4 py-3 text-sm text-paper outline-none focus:border-white/30"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="securedesk-waitlist-submit"
        className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-5 py-3 hover:bg-white transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : null}
        Reserve early access
      </button>
    </form>
  );
}

export default function ProductSecureDesk() {
  const launch = useLaunchStatus();
  const locked = launch?.locked;

  return (
    <PageLayout
      seoTitle="SecureDesk AI Support & Security Operations | Aliwer"
      seoDescription="SecureDesk pairs AI customer-support chatbots with security operations monitoring — always-on assistance and continuous threat detection from one dashboard."
      path="/products/securedesk"
      eyebrow="Product · SecureDesk"
      title="Support and security, always on."
      subtitle="SecureDesk combines AI customer-support chatbots with security operations monitoring for businesses — responsive assistance and continuous protection."
      testid="product-securedesk"
    >
      <div className="grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 rounded-2xl overflow-hidden">
        {features.map((f, i) => (
          <section key={i} className="bg-surface p-7">
            <h2 className="font-display text-lg tracking-tight text-paper mb-2">{f.heading}</h2>
            <p className="text-sm text-fog leading-relaxed">{f.body}</p>
          </section>
        ))}
      </div>

      <div className="mt-12">
        {launch == null ? null : locked ? (
          <>
            <EarlyAccess product="SecureDesk" launchDate={launch.launch_date} />
            <WaitlistForm />
          </>
        ) : (
          <a
            href="https://guardai-bot.emergent.host"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="securedesk-launch"
            className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-5 py-3 hover:bg-white transition-colors"
          >
            Launch SecureDesk
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        )}
      </div>
    </PageLayout>
  );
}
